import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useCurrentUser } from "../user/User"
import { Chart, registerables } from 'chart.js'
import { Bar, Pie } from 'react-chartjs-2'
import { useTime } from "../time/Time"
import { fetchData } from "../api/Axios"
import { countPerDay, dayKey, dayLabel, get, lastDays, sumPerDay, sumPerGroup, sumPerGroupAndDay } from "./DashboardData"

Chart.register(...registerables)

const Dashboard = () => {
  function filter(list, propertyKey, propertyValue) {
    return list.filter(entry => entry[propertyKey] === propertyValue)
  }

  function sum(list, propertyKey) {
    var sum = 0
    list.forEach(item => {
      sum += item[propertyKey]
    })
    return sum
  }

  function getRandomColor() {
    var letters = '0123456789ABCDEF'
    var color = '#'
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)]
    }
    return color
  }

  const [openTickets, setOpenTickets] = useState([])
  const [newTickets, setNewTickets] = useState([])
  const [newWarns, setNewWarns] = useState([])
  const [newFactions, setNewFactions] = useState([])
  const [newParticipants, setNewParticipants] = useState([])
  const [moneyMovements, setMoneyMovements] = useState([])
  const [moneyMovementLabels, setMoneyMovementLabels] = useState([])
  const [influenceChanges, setInfluenceChanges] = useState([])
  const [influenceChangeLabels, setInfluenceChangeLabels] = useState([])

  const [ticketCategoryChart, setTicketCategoryChart] = useState(null)
  const [clientChart, setClientChart] = useState(null)
  const [punishmentCategoryChart, setPunishmentCategoryChart] = useState(null)
  const [factionRankChart, setFactionRankChart] = useState(null)
  const [incomingMoneyCauseChart, setIncomingMoneyCauseChart] = useState(null)
  const [outgoingMoneyCauseChart, setOutgoingMoneyCauseChart] = useState(null)
  const [playerRankChart, setPlayerRankChart] = useState(null)
  const [characterGenderChart, setCharacterGenderChart] = useState(null)
  const [characterAgeChart, setCharacterAgeChart] = useState(null)
  const [playTimeChart, setPlayTimeChart] = useState(null)
  
  const [averageOnlineTime, setAvarageOnlineTime] = useState(0)
  const [onlineTimes, setOnlineTimes] = useState([])
  const [money, setMoney] = useState(0)
  const [influence, setInfluence] = useState(0)
  const [factions, setFactions] = useState(0)
  const [players, setPlayers] = useState(0)
  const { time } = useTime(averageOnlineTime, 1)
  const [playersAboutToDelete, setPlayersAboutToDelete] = useState(0)
  const [averagePlayersPerFaction, setAveragePlayersPerFaction] = useState(0)
  const [averageIpScore, setAverageIpScore] = useState(0)
  const [overallDoneActivities, setOverallDoneActivities] = useState(0)
  const [overallDoneQuests, setOverallDoneQuests] = useState(0)
  const [overallDeaths, setOverallDeaths] = useState(0)
  const [overallKills, setOverallKills] = useState(0)
  const [overallMobKills, setOverallMobKills] = useState(0)
  const [weeklyActivityLabels, setWeeklyActivityLabels] = useState([])
  const [weeklyActivity, setWeeklyActivity] = useState([])
  const [weeklyActivityHours, setWeeklyActivityHours] = useState(2)
  const [newPlayersWithinLast2Days, setNewPlayersWithinLast2Days] = useState([])
  const [activePlayersWithinLast7Days, setActivePlayersWithinLast7Days] = useState([])

  const { uuid } = useCurrentUser()
  if(uuid != null) {
    useEffect(() => {
      const abortCont = new AbortController()

      // Deleted tickets count for the statistics as well
      fetchData("/api/tickets?deleted=true", "Tickets konnten nicht geladen werden.", data => {
        setOpenTickets(filter(data, "state", "open"))
        let newData = []
        let ticketsPerDay = countPerDay(data, ticket => new Date(ticket.created))
        lastDays().forEach(date => {
          newData.push({
            label: dayLabel(date),
            data: [get(ticketsPerDay, dayKey(date))],
            backgroundColor: [getRandomColor()],
          })
        })
        setNewTickets(newData)
        
        let categoryLabels = [...new Set(data.map(ticket => ticket.category))]
        let categoryData = []
        let categoryColors = []
        categoryLabels.forEach((category) => {
          categoryData.push(filter(data, "category", category).length)
          categoryColors.push(getRandomColor())
        })
        setTicketCategoryChart(<Pie data={{
          labels: categoryLabels,
          datasets: [{
            data: categoryData,
            backgroundColor: categoryColors,
          }]
        }}/>)
      }, abortCont.signal);

      fetchData("/api/clients", "Clients konnten nicht geladen werden.", data => {
        let clientLabels = [...new Set(data.map(client => client.client))]
          let clientData = []
          let clientColors = []
          clientLabels.forEach((client) => {
            clientData.push(filter(data, "client", client).length)
            clientColors.push(getRandomColor())
          })
          setClientChart(<Pie data={{
            labels: clientLabels,
            datasets: [{
              data: clientData,
              backgroundColor: clientColors,
            }]
          }}/>)
      }, abortCont.signal);

      fetchData("/api/punishments", "Verwarnungen konnten nicht geladen werden.", data => {
        let newData = []
        let punishmentsPerDay = countPerDay(data, punishment => new Date(punishment.created * 1000))
        lastDays().forEach(date => {
          newData.push({
            label: dayLabel(date),
            data: [get(punishmentsPerDay, dayKey(date))],
            backgroundColor: [getRandomColor()],
          })
        })
        setNewWarns(newData)

        let categoryLabels = [...new Set(data.map(punishment => punishment.type))]
        let categoryData = []
        let categoryColors = []
        categoryLabels.forEach((type) => {
          categoryData.push(filter(data, "type", type).length)
          categoryColors.push(getRandomColor())
        })
        setPunishmentCategoryChart(<Pie data={{
          labels: categoryLabels,
          datasets: [{
            data: categoryData,
            backgroundColor: categoryColors,
          }]
        }}/>)
      }, abortCont.signal);

      fetchData("/api/onlinetime", "Spielzeit konnte nicht geladen werden.", data => {
        let averageOnlineTime = 0;
        data.forEach(playerEntry => {
          averageOnlineTime += playerEntry.online_time
        });
        setAvarageOnlineTime(averageOnlineTime / data.length)
        setOnlineTimes(data)

        let playTimeLabels = ["<1h", "1h-1d", "1d-1w", ">1w"]
        let playTimeData = []
        let playTimeColors = []
        playTimeLabels.forEach((label) => {
          switch (label) {
            case "<1h":
              playTimeData.push(data.filter(playerEntry => playerEntry.online_time < 3600).length)
              break;
            case "1h-1d":
              playTimeData.push(data.filter(playerEntry => playerEntry.online_time >= 3600 && playerEntry.online_time < 86400).length)
              break;
            case "1d-1w":
              playTimeData.push(data.filter(playerEntry => playerEntry.online_time >= 86400 && playerEntry.online_time < 604800).length)
              break;
            case ">1w":
              playTimeData.push(data.filter(playerEntry => playerEntry.online_time >= 604800).length)
              break;
          }
          playTimeColors.push(getRandomColor())
        })
        setPlayTimeChart(<Pie data={{
          labels: playTimeLabels,
          datasets: [{
            data: playTimeData,
            backgroundColor: playTimeColors,
          }]
        }}/>)
      }, abortCont.signal);

      fetchData("/api/participants", "Spieler konnten nicht geladen werden.", data => {
        setPlayers(data.length)

        let newData = []
        let participantsPerDay = countPerDay(data, participant => new Date(participant.created))
        lastDays().forEach(date => {
          newData.push({
            label: dayLabel(date),
            data: [get(participantsPerDay, dayKey(date))],
            backgroundColor: [getRandomColor()],
          })
        })
        setNewParticipants(newData)
        
        let rankLabels = [...new Set(data.map(participant => participant.rank))]
        let rankData = []
        let rankColors = []
        rankLabels.forEach((rank) => {
          rankData.push(filter(data, "rank", rank).length)
          rankColors.push(getRandomColor())
        })
        setPlayerRankChart(<Pie data={{
          labels: rankLabels,
          datasets: [{
            data: rankData,
            backgroundColor: rankColors,
          }]
        }}/>)

        fetchData("/api/factions", "Gefolge konnten nicht geladen werden.", data => {
          setFactions(data.length)
          let currentInfluence = sum(data, "influence")
          setInfluence(currentInfluence)

          let players = 0
          if(data.length > 0) {
            data.forEach((faction) => {
              players += faction.member_count
            })
            players = Number(players / data.length).toFixed(2)
          }
          setAveragePlayersPerFaction(players)

          let newData = []
          let factionsPerDay = countPerDay(data, faction => new Date(faction.creation_date_timestamp))
          lastDays().forEach(date => {
            newData.push({
              label: dayLabel(date),
              data: [get(factionsPerDay, dayKey(date))],
              backgroundColor: [getRandomColor()],
            })
          })
          setNewFactions(newData)

          let categoryLabels = [...new Set(data.map(faction => faction.rank_name))]
          let categoryData = []
          let categoryColors = []
          categoryLabels.forEach((rank_name) => {
            categoryData.push(filter(data, "rank_name", rank_name).length)
            categoryColors.push(getRandomColor())
          })
          setFactionRankChart(<Pie data={{
            labels: categoryLabels,
            datasets: [{
              data: categoryData,
              backgroundColor: categoryColors,
            }]
          }}/>)

          fetchData("/api/influences", "Einflussveränderungen konnten nicht geladen werden.", data => {
            let categoryLabels = [...new Set(data.map(influenceChange => influenceChange.reason))]

            let newData = []
            let newInfluenceChangeLabels = []
            let overallInfluenceData = []

            let days = lastDays()
            let toTimestamp = item => new Date(item.timestamp)
            let influencePerDay = sumPerDay(data, toTimestamp, "influence")
            let influencePerReason = sumPerGroupAndDay(data, toTimestamp, "reason", "influence")

            days.forEach(date => {
              newInfluenceChangeLabels.push(dayLabel(date))
              currentInfluence += get(influencePerDay, dayKey(date))
              overallInfluenceData.push(currentInfluence)
            })

            categoryLabels.forEach(categoryLabel => {
              let currentCaseData = []
              let perDay = influencePerReason.get(categoryLabel) || new Map()
              days.forEach(date => {
                currentCaseData.push(get(perDay, dayKey(date)))
              })
              newData.push({
                label: categoryLabel,
                data: currentCaseData,
                backgroundColor: [getRandomColor()],
              })
            })

            newData.push({
              label: "Einfluss im System",
              data: overallInfluenceData,
              backgroundColor: [getRandomColor()],
              type: 'line',
            })

            setInfluenceChangeLabels(newInfluenceChangeLabels)
            setInfluenceChanges(newData)
          })
        })

        fetchData("/api/characters", "Charaktere konnten nicht geladen werden.", data => {
          setPlayersAboutToDelete(data.filter(char => char.delete_in <= 604800).length)

          const twoDaysAgo = new Date(new Date().getTime() - 2 * 24 * 60 * 60 * 1000).getTime()
          const sevenDaysAgo = new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000).getTime()
          setNewPlayersWithinLast2Days(data.filter(char => char.first_played >= twoDaysAgo).map(char => char.player_name))
          setActivePlayersWithinLast7Days(data.filter(char => char.last_played >= sevenDaysAgo).map(char => char.player_name))

          let genderLabels = [...new Set(data.map(character => character.geschlecht))]
          let genderData = []
          let genderColors = []
          genderLabels.forEach((geschlecht) => {
            genderData.push(filter(data, "geschlecht", geschlecht).length)
            genderColors.push(getRandomColor())
          })
          setCharacterGenderChart(<Pie data={{
            labels: genderLabels,
            datasets: [{
              data: genderData,
              backgroundColor: genderColors,
            }]
          }}/>)

          let ageLabels = [...new Set(data.map(character => character.alter))]
          let ageData = []
          let ageColors = []
          ageLabels.forEach((alter) => {
            ageData.push(filter(data, "alter", alter).length)
            ageColors.push(getRandomColor())
          })
          setCharacterAgeChart(<Pie data={{
            labels: ageLabels,
            datasets: [{
              data: ageData,
              backgroundColor: ageColors,
            }]
          }}/>)
        })
      }, abortCont.signal);

      fetchData("/api/addresses", "IP Adressen konnten nicht geladen werden.", data => {
        let overallScore = 0
        data.forEach((ip) => {
          overallScore += ip.score
        })
        setAverageIpScore(Number(overallScore / data.length).toFixed(0))
      })

      fetchData("/api/moneymovements", "Geldbewegungen konnten nicht geladen werden.", data => {
        let categoryLabels = [...new Set(data.map(moneyMovement => moneyMovement.cause))]
        
        let overallMoney = 0
        data.forEach((moneyMovement) => {
          if(moneyMovement.direction == "IN") {
            overallMoney += moneyMovement.value
          } else {
            overallMoney -= moneyMovement.value
          }
        })
        setMoney(overallMoney)

        let days = lastDays()
        let toCreated = moneyMovement => new Date(moneyMovement.created)
        let incoming = filter(data, "direction", "IN")
        let outgoing = filter(data, "direction", "OUT")
        let inPerDay = sumPerDay(incoming, toCreated, "value")
        let outPerDay = sumPerDay(outgoing, toCreated, "value")
        let inPerCause = sumPerGroupAndDay(incoming, toCreated, "cause", "value")
        let outPerCause = sumPerGroupAndDay(outgoing, toCreated, "cause", "value")

        // The chart starts with the balance of 31 days ago, so the movements since then are taken back out.
        days.forEach(date => {
          overallMoney -= get(inPerDay, dayKey(date))
          overallMoney += get(outPerDay, dayKey(date))
        })

        let newData = []
        let newMoneyMovementLabels = []
        let overallMoneyData = []
        days.forEach(date => {
          newMoneyMovementLabels.push(dayLabel(date))
          overallMoney += get(inPerDay, dayKey(date)) - get(outPerDay, dayKey(date))
          overallMoneyData.push(overallMoney)
        })
        categoryLabels.forEach((categoryLabel) => {
          let currentCaseData = []
          let causeIn = inPerCause.get(categoryLabel) || new Map()
          let causeOut = outPerCause.get(categoryLabel) || new Map()
          days.forEach(date => {
            currentCaseData.push(get(causeIn, dayKey(date)) - get(causeOut, dayKey(date)))
          })
          newData.push({
            label: categoryLabel,
            data: currentCaseData,
            backgroundColor: [getRandomColor()],
          })
        })
        newData.push({
          label: "Geld im System",
          data: overallMoneyData,
          backgroundColor: [getRandomColor()],
          type: 'line',
        })
        setMoneyMovementLabels(newMoneyMovementLabels)
        setMoneyMovements(newData)

        let inCategoryData = []
        let outCategoryData = []
        let categoryColors = []
        let inTotalPerCause = sumPerGroup(incoming, "cause", "value")
        let outTotalPerCause = sumPerGroup(outgoing, "cause", "value")
        categoryLabels.forEach((category) => {
          inCategoryData.push(get(inTotalPerCause, category))
          outCategoryData.push(get(outTotalPerCause, category))
          categoryColors.push(getRandomColor())
        })
        setIncomingMoneyCauseChart(<Pie data={{
          labels: categoryLabels,
          datasets: [{
            data: inCategoryData,
            backgroundColor: categoryColors,
          }]
        }}/>)
        setOutgoingMoneyCauseChart(<Pie data={{
          labels: categoryLabels,
          datasets: [{
            data: outCategoryData,
            backgroundColor: categoryColors,
          }]
        }}/>)
      })

      fetchData("/api/onlinetime/weeklyactive?weeks=52", "Wochenstatistik konnte nicht geladen werden.", data => {
        let weeks = data.weeks || []
        setWeeklyActivityHours(Math.round((data.min_seconds / 3600) * 10) / 10)
        setWeeklyActivityLabels(weeks.map(week => week.week))
        setWeeklyActivity([{
          label: "Aktive Spieler",
          data: weeks.map(week => week.active_players),
          // Die laufende Woche ist noch nicht zu Ende und saehe sonst wie ein Einbruch aus
          backgroundColor: weeks.map(week => week.partial ? "#9ec5e8" : "#3c8dbc"),
        }, {
          label: "Neue Spieler",
          data: weeks.map(week => week.new_players),
          backgroundColor: weeks.map(week => week.partial ? "#b8e0b8" : "#00a65a"),
        }])
      }, abortCont.signal)

      fetchData("/api/stats?format=int&stats=Quest", "Quest Statistiken konnten nicht geladen werden.", data => setOverallDoneQuests(data.amount))
      fetchData("/api/stats?format=int&stats=Aktivität", "Aktivität Statistiken konnten nicht geladen werden.", data => setOverallDoneActivities(data.amount))
      
      fetchData("/api/mcstats?stats=DEATHS,PLAYER_KILLS,MOB_KILLS", "Statistiken konnten nicht geladen werden.", data => {
        setOverallDeaths(data.DEATHS)
        setOverallKills(data.PLAYER_KILLS)
        setOverallMobKills(data.MOB_KILLS)
      })

      return () => abortCont.abort()
    }, [uuid])
  }

  return (
    <div className="dashboard">
      { /* Supporter stats */ }
      <div className="row">
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-" + (openTickets.length === 0  ? "success" : openTickets.length > 10 ? "danger" : "warning")}>
            <div className="inner">
              <h3>{openTickets.length}</h3>
              <p>Offene Tickets</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-clipboard-list-check"></i>
            </div>
            <Link to="/ticket" className="small-box-footer">Tickets anzeigen <i className="fas fa-arrow-circle-right"></i></Link>
          </div>
        </div>
        
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-" + (playersAboutToDelete == 0  ? "success" : playersAboutToDelete.length > 10 ? "danger" : "warning")}>
            <div className="inner">
              <h3>{playersAboutToDelete}</h3>
              <p>Charakterlöschungen diese Woche</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-trash"></i>
            </div>
            <Link to="/character" className="small-box-footer">Charaktere anzeigen <i className="fas fa-arrow-circle-right"></i></Link>
          </div>
        </div>
        
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{averageIpScore}</h3>
              <p>Durchschnittlicher IP Score</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-map-location"></i>
            </div>
            <Link to="/address" className="small-box-footer">IP Adressen anzeigen <i className="fas fa-arrow-circle-right"></i></Link>
          </div>
        </div>
      </div>
      
      { /* Dynasty stats */ }
      <div className="row">
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{factions}</h3>
              <p>Gefolge</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-campground"></i>
            </div>
            <Link to="/faction" className="small-box-footer">Gefolge anzeigen <i className="fas fa-arrow-circle-right"></i></Link>
          </div>
        </div>
        
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{averagePlayersPerFaction}</h3>
              <p>Durchschnittliche Spieleranzahl pro Gefolge</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-gauge"></i>
            </div>
            <Link to="/participant" className="small-box-footer">Spieler anzeigen <i className="fas fa-arrow-circle-right"></i></Link>
          </div>
        </div>
        
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{overallDoneQuests}</h3>
              <p>Erledigte Quests</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-block-question"></i>
            </div>
          </div>
        </div>
        
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{overallDoneActivities}</h3>
              <p>Erledigte Aktivitäten</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-wave-pulse"></i>
            </div>
          </div>
        </div>
      </div>
      
      { /* Player stats */ }
      <div className="row">
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{players}</h3>
              <p>Spieler</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-gamepad-modern"></i>
            </div>
            <Link to="/participant" className="small-box-footer">Spieler anzeigen <i className="fas fa-arrow-circle-right"></i></Link>
          </div>
        </div>

        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{time}</h3>
              <p>Durchschnittliche Spielzeit</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-timer"></i>
            </div>
            <Link to="/onlinetime" className="small-box-footer">Spielzeit anzeigen <i className="fas fa-arrow-circle-right"></i></Link>
          </div>
        </div>
        
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{money}</h3>
              <p>Gesamtes Geld</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-dollar-sign"></i>
            </div>
            <Link to="/moneymovement" className="small-box-footer">Geldbewegungen anzeigen <i className="fas fa-arrow-circle-right"></i></Link>
          </div>
        </div>
      </div>
      
      { /* Player detail stats */ }
      <div className="row">
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{overallDeaths}</h3>
              <p>Tode</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-skull"></i>
            </div>
          </div>
        </div>
        
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{overallKills}</h3>
              <p>Tötungen</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-bullseye-arrow"></i>
            </div>
          </div>
        </div>
        
        <div className="col-lg-3 col-6">
          <div className={"small-box bg-success"}>
            <div className="inner">
              <h3>{overallMobKills}</h3>
              <p>Mob Tötungen</p>
            </div>
            <div className="icon">
            <i className="nav-icon fa-solid fa-paw"></i>
            </div>
          </div>
        </div>
      </div>

      <h2>Tickets</h2>
      <div className="row">
        <div className="col-xl-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Neue Tickets</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: ['Neue Tickets'],
                datasets: newTickets
              }}/>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Ticket Kategorien</h3>
            </div>
            <div className="card-body">
              {ticketCategoryChart}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Clients</h3>
            </div>
            <div className="card-body">
              {clientChart}
            </div>
          </div>
          </div>
      </div>

      <h2>Verwarnungen</h2>
      <div className="row">
        <div className="col-xl-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Neue Verwarnungen</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: ['Neue Verwarnungen'],
                datasets: newWarns
              }}/>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Verwarnungstypen</h3>
            </div>
            <div className="card-body">
              {punishmentCategoryChart}
            </div>
          </div>
        </div>
      </div>

      <h2>Gefolge</h2>
      <div className="row">
        <div className="col-xl-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Neue Fraktionen</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: ['Neue Fraktionen'],
                datasets: newFactions
              }}/>
            </div>
          </div>
        </div>
        
        <div className="col-xl-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Neue Spieler</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: ['Neue Spieler'],
                datasets: newParticipants
              }}/>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Gefolgeränge</h3>
            </div>
            <div className="card-body">
              {factionRankChart}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Spielerränge</h3>
            </div>
            <div className="card-body">
              {playerRankChart}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Geschlechtsverteilung</h3>
            </div>
            <div className="card-body">
              {characterGenderChart}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Altersstufen</h3>
            </div>
            <div className="card-body">
              {characterAgeChart}
            </div>
          </div>
        </div>
      </div>

      <h2>Geld</h2>
      <div className="row">
        <div className="col-xl-12 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Letzte Geldbewegungen</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: moneyMovementLabels,
                datasets: moneyMovements
              }} options={{
                scales: {
                  x: {
                    stacked: true
                  }, y: {
                    stacked: true
                  }
                }
              }}/>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Eingehendes Geld</h3>
            </div>
            <div className="card-body">
              {incomingMoneyCauseChart}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Ausgehendes Geld</h3>
            </div>
            <div className="card-body">
              {outgoingMoneyCauseChart}
            </div>
          </div>
        </div>
      </div>

      <h2>Einfluss</h2>
      <div className="row">
        <div className="col-xl-12 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Letzte Einflussveränderungen</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: influenceChangeLabels,
                datasets: influenceChanges
              }} options={{
                scales: {
                  x: {
                    stacked: true
                  }, y: {
                    stacked: true
                  }
                }
              }}/>
            </div>
          </div>
        </div>
      </div>

      <h2>Spieler</h2>
      <div className="row">
        <div className="col-xl-6 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Neue Spieler der letzten 2 Tage</h3>
            </div>
            <div className="card-body">
              {newPlayersWithinLast2Days.map(name => <span key={name}>{name}<br></br></span>)}
            </div>
          </div>
        </div>

        <div className="col-xl-6 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Aktive Spieler der letzten 7 Tage</h3>
            </div>
            <div className="card-body">
              {activePlayersWithinLast7Days.map(name => <span key={name}>{name}<br></br></span>)}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Spielzeiten</h3>
            </div>
            <div className="card-body">
              {playTimeChart}
            </div>
          </div>
        </div>

        <div className="col-xl-12 col-12">
          <div className="card card-success">
            <div className="card-header">
              <h3 className="card-title">Aktive Spieler je Kalenderwoche</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: weeklyActivityLabels,
                datasets: weeklyActivity
              }} options={{
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      precision: 0
                    }
                  }
                }
              }}/>
              <small className="text-muted">
                Als aktiv gilt, wer in der Woche mindestens {weeklyActivityHours} Stunden online war.
                Die laufende Woche ist heller dargestellt, weil sie noch nicht zu Ende ist. Eine Sitzung
                zaehlt in die Woche, in der sich der Spieler abgemeldet hat.
              </small>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
 
export default Dashboard